// Approve T&M Pricing (slice 1d + P2-d) — the modal on a PENDING Time & Materials sales order.
//
// Flow: GET tm_pricing_context + tm_line_actuals?evidence=0 + tm_defects (in parallel) → price the
// lines ("From actuals" or "Price by hand") → [Preview] POST tm_runs (a draft, no PIN, writes no
// price) → [Approve T&M Pricing] (only while the last Preview was ready_to_approve AND the inputs
// still match it) → the confirm step → the normal PIN prompt (auto-fill allowed, decision 13) →
// POST tm_finalize {run_id, proposal_hash, pin}.
//
// Bases (decision 14): "From actuals" (basis so_line_actuals: every line's own shop time, material,
// gas + wear and outside processing; the suggestion or a unit of your own with a reason; extras and
// the order minimum; the Scrap drill-down with shop-error decisions and remake links) is the default
// when the actuals are available; "Price by hand" (the 1c manual basis) is ALWAYS a peer tab. A
// manual draft acknowledges only the 1c codes; when the actuals are unavailable the window opens on
// "Price by hand" and says why (actuals_note / collector_failed).
//
// A 409 actuals_changed on tm_runs carries the fresh actuals: they replace the loaded ones, the lines
// that moved are highlighted, what was typed stays, the Preview is dropped, and the full actuals are
// re-read in the background. A 409 at tm_finalize re-reads all three.
//
// Money goes to the API as exact 2-dp strings; every total shown after Preview is the server's.
//
// Structure: TmPricingModal is only the shell (the Bootstrap Modal). Everything that holds state
// or computes lives in TmPricingBody, inside the window's own ErrorBoundary, so a render crash in
// the body shows an in-window message (with Close) instead of unmounting the whole modal.
//
// onClose(outcome): outcome is { outcome: 'unknown' } when a finalize was sent and its result is
// not known (no answer, 503, or a 409 whose cause was not confirmed): the page re-reads the order.
//
// The body is keyed by salesOrderId (another order starts from nothing) and the window ignores
// Escape (a stray key would throw away every typed price, reason and extra); Close / × still close.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import ErrorBoundary from '../ErrorBoundary.jsx';
import pinPrompt from '../../utils/pinPrompt';
import { tmRequest } from '../../utils/tmApi';
import { fmtMoney, fmtDelta } from '../../utils/priceMath';
import {
  PHASE1_ACK_CODES, actualsDiff, actualsSuggestionKey, applyChangedActuals, approveRows, buildActualsRunBody,
  buildManualRunBody, chipLabel, extraToRequest, extrasFromProposal, initialManualInput, linkQty, manualLineView,
  money2, newExtraInput, previewSignature, groupReadiness, severityOf, splitLineErrors, toCents,
} from '../../utils/tmPricing';
import TmReadinessList from './TmReadinessList.jsx';
import TmReferenceCard from './TmReferenceCard.jsx';
import TmManualLineTable from './TmManualLineTable.jsx';
import TmActualsLineTable from './TmActualsLineTable.jsx';
import TmExtrasCard from './TmExtrasCard.jsx';
import TmScrapDrilldown, { defectsForLine } from './TmScrapDrilldown.jsx';
import TmDefectDecisionDialog from './TmDefectDecisionDialog.jsx';
import TmApproveConfirm from './TmApproveConfirm.jsx';

// The pricing bases: "From actuals" is the default when the actuals are available; "Price by hand"
// is always offered (decision 14).
const BASES = [{ key: 'actuals', label: 'From actuals' }, { key: 'manual', label: 'Price by hand' }];
const ACTUALS_BASIS = 'so_line_actuals';

const HEADER_KEYS = ['shipping_amount', 'additional_fees', 'discount_amount'];
const TITLE_ID = 'tm-pricing-title';

/**
 * Header money inputs from a context / draft header. A missing value starts at 0.00; a stored value
 * that is not exact to the cent (discount_amount is an unconstrained numeric: "12.345") is kept AS
 * TYPED so the Preview check refuses it, never silently replaced by 0.00.
 */
export function headerFrom(h) {
  const out = {};
  HEADER_KEYS.forEach((k) => {
    const v = h ? h[k] : null;
    out[k] = v == null ? '0.00' : (money2(v) ?? String(v));
  });
  return out;
}

function ErrorBox({ error, onRetry, retryLabel = 'Retry' }) {
  if (!error) return null;
  return (
    <div className="alert alert-danger py-2 small" role="alert">
      <div>{error.text}</div>
      {Array.isArray(error.errors) && error.errors.length > 0 && (
        <ul className="mb-0 ps-3">{error.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
      )}
      {onRetry && <button type="button" className="btn btn-sm btn-outline-danger mt-1" onClick={onRetry}>{retryLabel}</button>}
    </div>
  );
}

const OTHER_SHARE_LABEL = { opo_value: 'outside processing', gas_wear: 'gas + wear', bar_material_cost: 'bar material cost' };

/** tm_line_actuals' reconciliation + totals: a dropped-task check, never proof of the split. */
function ReconciliationFooter({ actuals }) {
  const t = (actuals && actuals.totals) || {};
  const rec = (actuals && actuals.reconciliation) || {};
  const other = (actuals && actuals.other_share) || {};
  const byStation = Array.isArray(rec.by_station) ? rec.by_station : [];
  const off = byStation.filter((r) => Number(r.residual) > 0);
  const mat = rec.material || {};
  const otherKeys = Object.keys(other).filter((k) => Number(other[k]));
  return (
    <div className="border-top pt-2 small text-muted" data-testid="tm-reconciliation">
      <div className="d-flex flex-wrap gap-3">
        <span>Lines total V <strong>{fmtMoney(t.V)}</strong></span>
        <span>Suggested units bill {fmtMoney(t.extended)} (rounding {fmtMoney(t.residual)})</span>
        <span>Direct cost {fmtMoney(t.direct_cost)} (no burden)</span>
        {t.measured_share != null && <span>{(Number(t.measured_share) * 100).toFixed(0)}% of the hours punched</span>}
      </div>
      {otherKeys.length > 0 && (
        <div title="Work on this order's jobs and shared sheets that belongs to other orders' parts: each order pays its own share.">
          Not billed here (other orders' parts on the same sheets and jobs): {otherKeys.map((k) => `${OTHER_SHARE_LABEL[k] || k.replace(/_/g, ' ')} ${k.endsWith('hours') ? Number(other[k]).toFixed(2) : fmtMoney(other[k])}`).join(' · ')}
        </div>
      )}
      <div>
        {off.length === 0 && !(Number(mat.residual) > 0)
          ? 'Reconciliation: every task hour and sheet dollar landed on a line or is accounted for.'
          : `Reconciliation: ${[...off.map((r) => `${r.station} ${Number(r.residual).toFixed(2)} h`), ...(Number(mat.residual) > 0 ? [`material ${fmtMoney(mat.residual)}`] : [])].join(', ')} landed nowhere.`}
        {rec.note ? <span title={rec.note}> <i className="bi bi-info-circle"></i></span> : null}
      </div>
    </div>
  );
}

const snapshot = (...parts) => JSON.stringify(parts);
const isObj = (x) => !!x && typeof x === 'object' && !Array.isArray(x);
const list = (x) => (Array.isArray(x) ? x : []);            // a server list that may come back malformed

function TmPricingBody({ salesOrderId, orderNumber, soLines, onApproved, requestClose, busyRef, outcomeRef }) {
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [reloadTick, setReloadTick] = useState(0);
  const [basis, setBasis] = useState('manual');
  const [basisReady, setBasisReady] = useState(false);
  const [basisNote, setBasisNote] = useState('');          // why the actuals are not used (manual default / collector_failed)
  const [inputs, setInputs] = useState({});
  const [headerIn, setHeaderIn] = useState(headerFrom(null));
  const [acks, setAcks] = useState({});
  const [note, setNote] = useState('');
  // "From actuals"
  const [actuals, setActuals] = useState(null);             // GET tm_line_actuals?evidence=0
  const [actualsState, setActualsState] = useState('loading'); // 'loading' | 'ok' | 'error'
  const [actualsError, setActualsError] = useState('');
  const [actualsTick, setActualsTick] = useState(0);
  const [defectsData, setDefectsData] = useState(null);     // GET tm_defects (null: use the cost data's own defects)
  const [defectsError, setDefectsError] = useState('');
  const [editableKnown, setEditableKnown] = useState(true);  // tm_defects.editable, kept across a failed re-read
  const [actInputs, setActInputs] = useState({});           // so_line_id -> {unit, reason, noCharge}
  const [extraRows, setExtraRows] = useState([]);
  const [minimum, setMinimum] = useState({ amount: '', reason: '' });
  const [moved, setMoved] = useState([]);                   // lines a 409 actuals_changed moved
  const [expanded, setExpanded] = useState([]);
  const [decision, setDecision] = useState(null);           // { defect, mode } → TmDefectDecisionDialog
  const [notice, setNotice] = useState(null);               // { kind, text } after a decision / link write
  // shared
  const [preview, setPreview] = useState(null);             // { data, signature, basis }
  const [readinessOverride, setReadinessOverride] = useState(null);
  const [runError, setRunError] = useState(null);           // { text, errors, retry }
  const [lineErrors, setLineErrors] = useState({});         // so_line_id -> [server 400 text]
  const [busy, setBusyState] = useState('');
  const [step, setStep] = useState('edit');                 // 'edit' | 'confirm' | 'done'
  const [finalizeError, setFinalizeError] = useState(null); // { text, checkAgain }
  const [result, setResult] = useState(null);
  const retryRef = useRef(null);                            // {run_id, proposal_hash, pin}: Check again only
  const initializedFor = useRef(null);
  const initialSnapshot = useRef(null);                     // the inputs as first loaded (resume confirm)
  const keepMovedRef = useRef(false);                       // the next actuals re-read keeps the highlight
  const ctxRef = useRef(null);                              // the context as last rendered (for the re-read)

  const setBusy = (b) => { busyRef.current = b; setBusyState(b); };

  const applyContext = (data) => {
    setCtx(data);
    const lines = Array.isArray(data.lines) ? data.lines : [];
    if (initializedFor.current !== salesOrderId) {
      initializedFor.current = salesOrderId;
      const init = {};
      lines.forEach((l) => { init[l.so_line_id] = initialManualInput(l); });
      const hdr = headerFrom(data.header);
      setInputs(init);
      setHeaderIn(hdr);
      setAcks({});
      setNote('');
      initialSnapshot.current = snapshot(init, hdr, {}, '', {}, [], { amount: '', reason: '' });
    } else {
      // A reload (after a 409) keeps what was typed; new lines get a starting input.
      setInputs((prev) => {
        const next = { ...prev };
        lines.forEach((l) => { if (!next[l.so_line_id]) next[l.so_line_id] = initialManualInput(l); });
        return next;
      });
    }
  };

  useEffect(() => {
    if (!salesOrderId) return undefined;
    let alive = true;
    setLoading(true);
    setLoadError('');
    tmRequest('GET', `/sales_orders/${salesOrderId}/tm_pricing_context`).then((r) => {
      if (!alive) return;
      setLoading(false);
      if (!r.ok) { setLoadError(r.text); return; }
      applyContext(isObj(r.data) ? r.data : {});
    });
    return () => { alive = false; };
    // applyContext only reads refs and setters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salesOrderId, reloadTick]);

  // The actuals and the defects, read in parallel with the context (each runs the collector).
  useEffect(() => {
    if (!salesOrderId) return undefined;
    let alive = true;
    const keepMoved = keepMovedRef.current;
    keepMovedRef.current = false;
    setActualsState((st) => (st === 'ok' ? 'ok' : 'loading'));
    setActualsError('');
    tmRequest('GET', `/sales_orders/${salesOrderId}/tm_line_actuals?evidence=0`).then((r) => {
      if (!alive) return;
      if (r.ok && isObj(r.data)) {
        setActuals(r.data);
        setActualsState('ok');
        if (!keepMoved) setMoved([]);
        // Usable actuals (e.g. after a Retry): "Price by hand" no longer says they are unavailable,
        // unless the context itself says so.
        const usable = r.data.actuals_available === true && Array.isArray(r.data.lines) && !!r.data.actuals_hash;
        if (usable && !(ctxRef.current && ctxRef.current.actuals_available === false)) setBasisNote('');
      } else {
        setActualsState('error');
        setActualsError(r.ok ? 'The cost data came back empty.' : r.text);
      }
    });
    tmRequest('GET', `/sales_orders/${salesOrderId}/tm_defects`).then((r) => {
      if (!alive) return;
      if (r.ok && isObj(r.data)) {
        setDefectsData(r.data);
        setDefectsError('');
        setEditableKnown(r.data.editable !== false);
      } else {
        // Never keep an older list next to fresh actuals: the drill-down falls back to the cost data's
        // own defects (tm_line_actuals' line.defects carry the decision, links and candidates).
        setDefectsData(null);
        setDefectsError(r.ok ? 'The defect list came back empty.' : r.text);
      }
    });
    return () => { alive = false; };
  }, [salesOrderId, actualsTick]);

  ctxRef.current = ctx;
  const actualsUsable = !!actuals && actuals.actuals_available === true && Array.isArray(actuals.lines) && !!actuals.actuals_hash;
  const collectorFailed = actuals && Array.isArray(actuals.readiness)
    ? actuals.readiness.find((it) => it && it.code === 'collector_failed') : null;
  const unavailableWhy = (ctx && ctx.actuals_note)
    || (collectorFailed && collectorFailed.detail)
    || (actualsState === 'error' ? actualsError : '')
    || (actuals && !actualsUsable ? 'The cost data is not available for this order.' : '');

  // The default basis, once: "From actuals" when both the context and the actuals say they are
  // available; otherwise "Price by hand", saying why.
  useEffect(() => {
    if (basisReady || !ctx || actualsState === 'loading') return;
    const ok = ctx.actuals_available === true && actualsUsable;
    setBasis(ok ? 'actuals' : 'manual');
    if (!ok) setBasisNote(unavailableWhy || 'The cost data is not available for this order.');
    setBasisReady(true);
  }, [basisReady, ctx, actualsState, actualsUsable, unavailableWhy]);

  const productBySoLine = useMemo(() => {
    const m = {};
    (soLines || []).forEach((l) => { if (l && l.sales_order_line_id != null) m[l.sales_order_line_id] = l.product_id; });
    return m;
  }, [soLines]);

  const ctxLines = useMemo(() => (ctx && Array.isArray(ctx.lines) ? ctx.lines : []), [ctx]);
  const actualsLines = useMemo(() => (actuals && Array.isArray(actuals.lines) ? actuals.lines : []), [actuals]);
  const ctxLineById = useMemo(() => {
    const m = {};
    ctxLines.forEach((l) => { m[l.so_line_id] = l; });
    return m;
  }, [ctxLines]);
  const labelOf = (l) => {
    const c = ctxLineById[l.so_line_id] || {};
    const pid = productBySoLine[l.so_line_id] || l.product_id || c.product_id;
    const desc = l.description || c.description;
    return [pid, desc].filter(Boolean).join(' — ') || `line ${l.so_line_id}`;
  };
  const labelOfId = (sid) => {
    const l = ctxLines.find((x) => String(x.so_line_id) === String(sid))
      || actualsLines.find((x) => String(x.so_line_id) === String(sid));
    return l ? labelOf(l) : `line ${sid}`;
  };

  // ── the two bodies ──
  const manualLines = ctxLines.map((l) => {
    const input = inputs[l.so_line_id] || {};
    const v = manualLineView(l, input);
    return { so_line_id: l.so_line_id, label: labelOf(l), unit_price: v.unit, no_charge_reason: input.reason, error: v.error };
  });
  const manualBuilt = buildManualRunBody({ lines: manualLines, header: headerIn, acks, note, ackCodes: PHASE1_ACK_CODES });

  // An acknowledgement is sent only for an ack item the window currently raises: the cost data's
  // readiness + warnings, the last actuals Preview's, and a 409's. One typed for an item that has
  // since gone away (e.g. the link was confirmed) is not recorded in the proposal.
  const actualsPreview = preview && preview.basis === 'actuals' ? preview.data : null;
  const raisedAckCodes = new Set([
    ...list(actuals && actuals.readiness), ...list(actuals && actuals.warnings),
    ...list(actualsPreview && actualsPreview.readiness), ...list(actualsPreview && actualsPreview.warnings),
    ...list(basis === 'actuals' && readinessOverride),
  ].filter((it) => it && typeof it === 'object' && severityOf(it) === 'ack').map((it) => it.code));
  // The last actuals Preview's per-line suggestions (after its extras): a typed unit on a line an
  // extra reaches is "the suggestion" only against these, and only while the Preview was made on the
  // same actuals and extras (buildActualsRunBody checks the key).
  const previewed = actualsPreview && preview.suggestKey ? {
    key: preview.suggestKey,
    suggested: Object.fromEntries(list(isObj(actualsPreview.proposal) ? actualsPreview.proposal.lines : null)
      .filter((pl) => pl && pl.so_line_id != null).map((pl) => [pl.so_line_id, pl.suggested_unit_price ?? null])),
  } : null;
  const actualsAckCodes = Object.keys(acks).filter((c) => raisedAckCodes.has(c));
  const minimumTyped = String(minimum.amount || '').trim() !== '' || String(minimum.reason || '').trim() !== '';
  const actualsBuilt = buildActualsRunBody({
    actualsHash: actuals && actuals.actuals_hash,
    lines: actualsLines.map((l) => {
      const inp = actInputs[l.so_line_id] || {};
      const zeroBill = (Number(l.qty && l.qty.billable) || 0) === 0;
      return {
        so_line_id: l.so_line_id, label: labelOf(l), unit_price: zeroBill ? '' : inp.unit,
        override_reason: inp.reason, no_charge_reason: inp.noCharge, zero_bill: zeroBill,
        supported: l.supported !== false, suggested_unit_price: l.suggested_unit_price ?? null,
      };
    }),
    header: headerIn,
    acks,
    note,
    extras: [
      ...extraRows.map(extraToRequest),
      ...(minimumTyped ? [{ kind: 'minimum_charge', amount: minimum.amount, reason: minimum.reason }] : []),
    ],
    ackCodes: actualsAckCodes,
    previewed,
  });
  if (basis === 'actuals' && !actualsUsable) actualsBuilt.errors.unshift('The cost data is not available: price the order by hand.');
  const built = basis === 'actuals' ? actualsBuilt : manualBuilt;
  const signature = previewSignature(built.body);
  const previewForBasis = preview && preview.basis === basis ? preview : null;
  const previewCurrent = !!previewForBasis && previewForBasis.signature === signature;
  const readiness = list(readinessOverride
    || (previewForBasis ? previewForBasis.data.readiness
      : (basis === 'actuals' ? (actuals && actuals.readiness) : (ctx && ctx.readiness))));
  const readinessWarnings = basis === 'actuals'
    ? list(previewForBasis ? previewForBasis.data.warnings : (actuals && actuals.warnings))
    : null;
  const canApprove = previewCurrent && !!previewForBasis.data.ready_to_approve;
  // Why the last Preview no longer matches: the cost data was re-read (its hash moved), or the inputs.
  const hashMoved = !!previewForBasis && basis === 'actuals' && !!actuals
    && previewForBasis.hash != null && previewForBasis.hash !== actuals.actuals_hash;
  const staleText = hashMoved
    ? 'The cost data changed after this Preview (a re-read came in). Preview again before approving.'
    : 'The inputs changed since this Preview. Preview again before approving.';
  const confirmBlocked = previewForBasis && !canApprove
    ? (hashMoved
      ? 'The cost data changed after this Preview (a re-read came in). Go Back and Preview again.'
      : 'This Preview can no longer be approved. Go Back and Preview again.')
    : null;
  const previewLinesById = useMemo(() => {
    if (!preview || !preview.data || !preview.data.proposal) return null;
    const m = {};
    (preview.data.proposal.lines || []).forEach((pl) => { m[pl.so_line_id] = pl; });
    return m;
  }, [preview]);

  const label = orderNumber || (ctx && ctx.order_number) || `SO #${salesOrderId}`;
  const hdr = (ctx && ctx.header) || {};
  const editable = defectsData ? defectsData.editable !== false : editableKnown;

  const clearLineError = (sid) => setLineErrors((prev) => {
    if (!prev[sid]) return prev;
    const next = { ...prev };
    delete next[sid];
    return next;
  });
  const changeLine = (sid, patch) => {
    setInputs((prev) => ({ ...prev, [sid]: { ...(prev[sid] || {}), ...patch } }));
    // The server's complaint about this line was about what it said before the edit.
    clearLineError(sid);
  };
  const changeActualsLine = (sid, patch) => {
    setActInputs((prev) => ({ ...prev, [sid]: { ...(prev[sid] || {}), ...patch } }));
    clearLineError(sid);
  };
  const switchBasis = (key) => {
    if (key === basis) return;
    setBasis(key);
    setReadinessOverride(null);
    setRunError(null);
    setLineErrors({});
  };

  // After a decision / link write (or a finalize 409): the actuals and defects are re-read, the
  // Preview no longer describes the order.
  const refreshActuals = ({ keepMoved = false, draftStale = false } = {}) => {
    keepMovedRef.current = keepMoved;
    setPreview(null);
    setActualsTick((t) => t + 1);
    // A decision / link changed the cost data: a saved actuals draft no longer matches it.
    if (draftStale) {
      setCtx((c) => (c && c.latest_draft && c.latest_draft.basis === ACTUALS_BASIS ? { ...c, latest_draft_stale: true } : c));
    }
  };

  const findDefect = (defectId) => {
    const list = defectsData && Array.isArray(defectsData.defects) ? defectsData.defects : [];
    const hit = list.find((d) => String(d.defect_id) === String(defectId));
    if (hit) return hit;
    for (const l of actualsLines) {
      const e = defectsForLine(l, null).find((d) => String(d.defect_id) === String(defectId));
      if (e) return e;
    }
    return null;
  };

  const linkDefect = async (req) => {
    const pin = await pinPrompt(`PIN to link job #${req.makeup_job_id} as the remake of defect #${req.defect_id} (${req.qty} pc):`);
    if (!pin) return false;
    setBusy('link');
    setNotice(null);
    const r = await tmRequest('POST', `/sales_orders/${salesOrderId}/tm_remake_links`, { ...req, pin });
    setBusy('');
    if (!r.ok) { setNotice({ kind: 'danger', text: r.text }); return false; }
    const d = r.data || {};
    const mj = d.makeup_job || {};
    setNotice(mj.kind === 'production' || d.warning
      ? { kind: 'warning', text: `Linked job #${req.makeup_job_id} to defect #${req.defect_id}. ${d.warning || 'It is treated as a PRODUCTION job: only the linked pieces come off, at its own cost.'}` }
      : { kind: 'success', text: `Linked job #${req.makeup_job_id} to defect #${req.defect_id} (${req.qty} pc) as its remake.` });
    refreshActuals({ draftStale: true });
    return true;
  };

  const removeLink = async (link, reason) => {
    const pin = await pinPrompt(`PIN to remove remake link #${link.link_id} (job #${link.makeup_job_id}):`);
    if (!pin) return false;
    setBusy('link');
    setNotice(null);
    const r = await tmRequest('DELETE', `/sales_orders/${salesOrderId}/tm_remake_links/${link.link_id}`, { pin, reason });
    setBusy('');
    if (!r.ok) { setNotice({ kind: 'danger', text: r.text }); return false; }
    setNotice({ kind: 'success', text: `Removed remake link #${link.link_id}.` });
    refreshActuals({ draftStale: true });
    return true;
  };

  const decisionDone = (data) => {
    const dec = (data && data.decision) || {};
    const d = decision && decision.defect;
    setDecision(null);
    setNotice({
      kind: 'success',
      text: `Defect #${(d && d.defect_id) || dec.defect_id}: ${dec.not_charged ? "shop error, not charged" : 'charged'}${data && data.changed === false ? ' (nothing changed)' : ''}.`,
    });
    refreshActuals({ draftStale: true });
  };

  const openScrap = (sid) => setExpanded((prev) => (prev.map(String).includes(String(sid)) ? prev : [...prev, sid]));
  const toggleExpanded = (sid) => setExpanded((prev) => (prev.map(String).includes(String(sid))
    ? prev.filter((x) => String(x) !== String(sid)) : [...prev, sid]));

  const moveValue = (l, amount) => setExtraRows((rows) => [...rows, newExtraInput('value_transfer', {
    from_so_line_id: String(l.so_line_id), amount: amount || (l.value && l.value.V) || '', reason: '',
  })]);

  // Cents typed in value transfers OUT of each 0-bill line (so its row stops offering "Move it").
  const transferred = {};
  extraRows.forEach((x) => {
    if (x.kind !== 'value_transfer' || x.from_so_line_id === '' || x.from_so_line_id == null) return;
    const c = toCents(x.amount);
    if (c !== null && c > 0) transferred[x.from_so_line_id] = (transferred[x.from_so_line_id] || 0) + c;
  });
  const ownPriceLines = actualsLines
    .filter((l) => String((actInputs[l.so_line_id] || {}).unit || '').trim() !== '')
    .map((l) => String(l.so_line_id));

  const doPreview = async () => {
    const runBasis = basis;
    setBusy('preview');
    setRunError(null);
    setLineErrors({});
    const r = await tmRequest('POST', `/sales_orders/${salesOrderId}/tm_runs`, built.body);
    setBusy('');
    if (r.ok) {
      const data = r.data || {};
      setPreview({
        data, signature, basis: runBasis, hash: runBasis === 'actuals' ? built.body.expected_actuals_hash : null,
        suggestKey: runBasis === 'actuals' ? actualsSuggestionKey(built.body) : null,
      });
      setReadinessOverride(null);
      // tm_runs discarded the older draft: the banner must not offer it any more.
      setCtx((c) => (c ? {
        ...c,
        latest_draft: {
          run_id: data.run_id, basis: data.basis || (runBasis === 'actuals' ? ACTUALS_BASIS : 'manual'),
          proposal: data.proposal, created_by: null,
        },
        latest_draft_stale: false,
      } : c));
      return;
    }
    setPreview(null);
    if (r.status === 409 && r.code === 'actuals_changed' && runBasis === 'actuals') {
      // The fresh numbers came with the refusal: show them, mark what moved, keep what was typed.
      const fresh = isObj(r.detail) ? r.detail.actuals : null;
      const hash = isObj(r.detail) ? r.detail.actuals_hash : null;
      const movedIds = actualsDiff(actuals, fresh);
      setActuals((prev) => applyChangedActuals(prev, fresh, hash));
      setMoved(movedIds);
      setRunError({
        text: `${r.text} ${movedIds.length ? `${movedIds.length} line${movedIds.length > 1 ? 's' : ''} moved (highlighted): look again, then Preview.` : 'Look again, then Preview.'}`,
        errors: [],
      });
      // The refusal proves a saved actuals draft no longer matches the cost data.
      refreshActuals({ keepMoved: true, draftStale: true });
      return;
    }
    if (r.status === 409 && r.code === 'collector_failed') {
      const why = (isObj(r.detail) && r.detail.message) || r.text;
      setBasis('manual');
      setBasisNote(why);
      setReadinessOverride(null);
      setRunError({ text: `${why} The window switched to "Price by hand".`, errors: [] });
      return;
    }
    if (r.status === 409 && isObj(r.detail) && Array.isArray(r.detail.readiness)) {
      setReadinessOverride(r.detail.readiness);
    }
    const split = splitLineErrors(r.errors, (runBasis === 'actuals' ? actualsLines : ctxLines).map((l) => l.so_line_id), labelOfId);
    setLineErrors(split.byLine);
    const onRows = Object.keys(split.byLine).length;
    const text = onRows > 0 ? `${r.text} See the marked line${onRows > 1 ? 's' : ''}.` : r.text;
    setRunError({ text, errors: split.rest, retry: r.status === 503 || r.status === 0 || r.status === 502 || r.status === 504 });
  };

  const doApprove = async (again = false) => {
    let payload = retryRef.current;
    if (!again) {
      if (!canApprove) {
        setFinalizeError({ text: confirmBlocked || 'This Preview can no longer be approved. Go Back and Preview again.', checkAgain: false });
        return;
      }
      // T&M approval is PIN-only on the API (_tm_active_user_from_pin never takes a face token).
      const pin = await pinPrompt(`Admin or Manager PIN to approve T&M pricing for ${label}:`, { face: false });
      if (!pin) return;
      payload = { run_id: previewForBasis.data.run_id, proposal_hash: previewForBasis.data.proposal_hash, pin };
      retryRef.current = payload;
    }
    if (!payload) return;
    setBusy('approve');
    setFinalizeError(null);
    const r = await tmRequest('POST', `/sales_orders/${salesOrderId}/tm_finalize`, payload);
    if (r.ok) {
      setBusy('');
      retryRef.current = null;
      outcomeRef.current = null;
      setResult(r.data || {});
      setStep('done');
      if (onApproved) onApproved(r.data || {});
      return;
    }
    if (r.status === 0 || r.status === 502 || r.status === 504) {
      // The server replays a finalized run (replayed: true), so asking again is safe.
      setBusy('');
      outcomeRef.current = 'unknown';
      setFinalizeError({ text: 'No answer. The approval may have gone through.', checkAgain: true });
      return;
    }
    if (r.status === 503) {
      setBusy('');
      outcomeRef.current = 'unknown';
      setFinalizeError({ text: r.text, checkAgain: true });
      return;
    }
    retryRef.current = null;
    if (r.status === 409) {
      // Either the order (or its cost data) changed since the Preview, or someone else approved it
      // first: look again. All three reads are redone.
      outcomeRef.current = 'unknown';
      const again409 = await tmRequest('GET', `/sales_orders/${salesOrderId}/tm_pricing_context`);
      setBusy('');
      const fresh = again409.ok && isObj(again409.data) ? again409.data : null;
      const fh = (fresh && fresh.header) || {};
      if (fh.tm_pricing_status === 'final') {
        outcomeRef.current = null;
        setCtx(fresh);
        setResult({ already_approved: true, priced_by: fh.tm_priced_by || null, priced_at: fh.tm_priced_at || null });
        setStep('done');
        if (onApproved) onApproved({ already_approved: true, priced_by: fh.tm_priced_by || null });
        return;
      }
      setPreview(null);
      setStep('edit');
      // The rebuild's own errors (e.g. a line whose suggestion moved) go on their rows; the message
      // already ends "preview again." when the server said so.
      const split = splitLineErrors(r.errors, (basis === 'actuals' ? actualsLines : ctxLines).map((l) => l.so_line_id), labelOfId);
      setLineErrors(split.byLine);
      const onRows = Object.keys(split.byLine).length;
      const said = /preview again\.?\s*$/i.test(r.text) ? r.text : `${r.text} Preview again.`;
      setRunError({ text: onRows > 0 ? `${said} See the marked line${onRows > 1 ? 's' : ''}.` : said, errors: split.rest });
      if (fresh) applyContext(fresh);
      else setReloadTick((t) => t + 1);
      refreshActuals();
      return;
    }
    setBusy('');
    setFinalizeError({ text: r.text, checkAgain: false });
  };

  const currentSnapshot = () => snapshot(inputs, headerIn, acks, note, actInputs, extraRows, minimum);

  const resumeDraft = () => {
    const d = ctx && ctx.latest_draft;
    const p = (d && d.proposal) || {};
    const typed = initialSnapshot.current !== null && currentSnapshot() !== initialSnapshot.current;
    // eslint-disable-next-line no-alert
    if (typed && !window.confirm(`Replace what you typed with draft #${d && d.run_id}?`)) return;
    const fromActuals = d && d.basis === ACTUALS_BASIS;
    if (fromActuals) {
      const next = {};
      (p.lines || []).forEach((pl) => {
        next[pl.so_line_id] = {
          unit: pl.price_source === 'tm_manual' ? (pl.unit_price ?? '') : '',
          reason: pl.override_reason || '',
          noCharge: pl.no_charge_reason || '',
        };
      });
      setActInputs(next);
      const x = extrasFromProposal(p);
      setExtraRows(x.rows);
      setMinimum(x.minimum);
      switchBasis('actuals');
    } else {
      setInputs((prev) => {
        const next = { ...prev };
        (p.lines || []).forEach((pl) => {
          next[pl.so_line_id] = { mode: 'unit', unit: pl.unit_price ?? '', total: '', reason: pl.no_charge_reason || '' };
        });
        return next;
      });
      switchBasis('manual');
    }
    if (p.header) setHeaderIn(headerFrom(p.header));
    const a = {};
    (p.acknowledgements || []).forEach((x) => { if (fromActuals || PHASE1_ACK_CODES.includes(x.code)) a[x.code] = x.reason; });
    setAcks(a);
    setNote(p.note || '');
    setPreview(null);
    setLineErrors({});
  };

  const readinessActions = (it) => {
    if (basis !== 'actuals' || !it) return null;
    const entries = Array.isArray(it.items) ? it.items : [];
    const locked = !!busy || !editable;
    const btn = 'btn btn-sm btn-outline-primary py-0';
    if (it.code === 'override_link_unconfirmed') {
      return (
        <div className="d-flex flex-wrap gap-1 mt-1">
          {entries.map((e) => {
            const def = findDefect(e.defect_id);
            const pr = (def && def.pricing) || { open_qty: e.open_qty };
            return (Array.isArray(e.candidates) ? e.candidates : []).map((c) => {
              const q = linkQty(pr, c.job_id, c.pieces);
              return (
                <button key={`c-${e.defect_id}-${c.job_id}`} type="button" className={btn} disabled={locked || q < 1}
                  title={q < 1 ? 'Nothing left to link: remove the production link in the Scrap drill-down first' : undefined}
                  onClick={() => linkDefect({ defect_id: e.defect_id, makeup_job_id: c.job_id, part_id: (def && def.part_id) ?? undefined, qty: q, source: 'heuristic_confirmed' })}>
                  Confirm link: job #{c.job_id} → defect #{e.defect_id}{q > 0 ? ` (${q} pc)` : ''}
                </button>
              );
            }).concat(e.so_line_id != null ? [
              <button key={`s-${e.defect_id}`} type="button" className="btn btn-sm btn-link py-0" onClick={() => openScrap(e.so_line_id)}>Show the scrap</button>,
            ] : []);
          })}
        </div>
      );
    }
    if (it.code === 'decision_snapshot_mismatch' || it.code === 'decision_unpriced') {
      const mode = it.code === 'decision_unpriced' ? 'undo' : 'reconfirm';
      const ids = Array.from(new Set(entries.map((e) => e.defect_id).filter((x) => x != null)));
      return (
        <div className="d-flex flex-wrap gap-1 mt-1">
          {ids.map((id) => (
            <button key={id} type="button" className={btn} disabled={locked}
              onClick={() => setDecision({ defect: findDefect(id) || { defect_id: id }, mode })}>
              {mode === 'undo' ? `Undo: charge defect #${id}` : `Re-confirm defect #${id}`}
            </button>
          ))}
        </div>
      );
    }
    return null;
  };

  const draft = ctx && ctx.latest_draft;
  const draftIsManual = draft && (!draft.basis || draft.basis === 'manual');
  const draftResumable = draft && (draftIsManual || (draft.basis === ACTUALS_BASIS && actualsUsable));
  const rg = groupReadiness([...readiness, ...(readinessWarnings || [])], acks);
  const blockers = (previewForBasis && previewForBasis.data && previewForBasis.data.blockers_open) || {};
  const asList = (x) => (Array.isArray(x) ? x : (x ? [x] : []));
  const hardOpen = asList(blockers.hard);
  const unackedOpen = asList(blockers.unacknowledged);
  const pWarnings = previewForBasis ? asList(previewForBasis.data.proposal_warnings) : [];
  const ready = !!ctx && basisReady;

  return (
    <>
      <Modal.Header closeButton={!busy}>
        <Modal.Title id={TITLE_ID} style={{ fontSize: '1.1rem' }}>
          <i className="bi bi-calculator me-2"></i>Approve T&amp;M Pricing — {label}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {(loading || (ctx && !basisReady)) && !ready && (
          <div className="text-center py-4"><div className="spinner-border text-primary" role="status"></div><div className="small mt-2">Loading the order and its cost data…</div></div>
        )}
        {loadError && !ctx && <ErrorBox error={{ text: loadError }} onRetry={() => setReloadTick((t) => t + 1)} />}

        {ctx && step === 'done' && result && result.already_approved && (
          <div className="alert alert-info" role="status">
            <strong>Already approved</strong>{result.priced_by ? ` (by ${result.priced_by})` : ''}. {label} was priced by
            someone else while this window was open; nothing was changed here. The order page is refreshed.
          </div>
        )}
        {ctx && step === 'done' && result && !result.already_approved && (
          <div className="alert alert-success" role="status">
            <strong>Approved.</strong> {label} is priced: total {fmtMoney(result.approved && result.approved.total)}
            {result.priced_by ? ` (by ${result.priced_by})` : ''}{result.run_id ? `, run #${result.run_id}` : ''}.
            {result.replayed && <div className="small">It had already gone through; nothing was changed twice.</div>}
            <div className="small mt-1">Next: Create Invoice on the order (the one close-out invoice).</div>
          </div>
        )}

        {ctx && step === 'confirm' && previewForBasis && (
          <>
            <TmApproveConfirm
              orderNumber={label}
              preview={previewForBasis.data}
              rows={approveRows(previewForBasis.data.proposal, ctxLines, labelOfId)}
              busy={busy === 'approve'}
              blocked={confirmBlocked}
              onBack={() => { setStep('edit'); setFinalizeError(null); retryRef.current = null; }}
              onApprove={() => doApprove(false)}
            />
            {finalizeError && (
              <ErrorBox
                error={finalizeError}
                onRetry={finalizeError.checkAgain ? () => doApprove(true) : null}
                retryLabel="Check again"
              />
            )}
          </>
        )}

        {ready && step === 'edit' && (
          <>
            <div className="d-flex flex-wrap gap-3 small text-muted mb-2">
              {hdr.tm_estimate_total != null && <span>Internal estimate {fmtMoney(hdr.tm_estimate_total)}</span>}
              {hdr.tm_not_to_exceed != null && <span>Internal not-to-exceed {fmtMoney(hdr.tm_not_to_exceed)}</span>}
              {ctx.bill_type && <span>Bills: {ctx.bill_type}</span>}
            </div>

            {draft && !previewForBasis && (
              <div className={`alert ${ctx.latest_draft_stale ? 'alert-warning' : 'alert-secondary'} py-2 small d-flex align-items-center gap-2`}>
                <div className="flex-grow-1">
                  {ctx.latest_draft_stale
                    ? `Draft #${draft.run_id} is out of date: the order or its cost data changed after it was saved. Preview again.`
                    : `Draft #${draft.run_id} was saved${draft.created_by ? ` by ${draft.created_by}` : ''}${draft.basis === ACTUALS_BASIS ? ' (from actuals)' : ''}.`}
                </div>
                {draftResumable && (
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={resumeDraft} disabled={!!busy}>
                    Resume draft #{draft.run_id}
                  </button>
                )}
              </div>
            )}

            <ul className="nav nav-tabs mb-2" role="tablist">
              {BASES.map((b) => (
                <li className="nav-item" key={b.key} role="presentation">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={basis === b.key}
                    className={`nav-link ${basis === b.key ? 'active' : ''}`}
                    disabled={!!busy}
                    onClick={() => switchBasis(b.key)}
                  >
                    {b.label}{b.key === 'actuals' && !actualsUsable ? ' (unavailable)' : ''}
                  </button>
                </li>
              ))}
            </ul>

            <h6 className="mt-2">Readiness</h6>
            <TmReadinessList
              items={readiness}
              warnings={readinessWarnings}
              acks={acks}
              onAck={(code, reason) => setAcks((a) => ({ ...a, [code]: reason }))}
              ackCodes={basis === 'manual' ? PHASE1_ACK_CODES : null}
              actions={readinessActions}
            />

            {basis === 'manual' && (
              <>
                <h6 className="mt-2">Price by hand</h6>
                {basisNote && (
                  <div className="alert alert-secondary py-2 small" data-testid="tm-actuals-note">
                    Pricing from actuals is not available: {basisNote}
                  </div>
                )}
                <TmReferenceCard reference={ctx.reference} />
                {ctx.preview_error && <div className="alert alert-danger py-2 small">Invoice preview failed: {String(ctx.preview_error)}</div>}
                <TmManualLineTable
                  lines={ctxLines}
                  inputs={inputs}
                  onChange={changeLine}
                  labelOf={labelOf}
                  previewLines={previewCurrent ? previewLinesById : null}
                  serverErrors={lineErrors}
                  disabled={!!busy}
                />
              </>
            )}

            {basis === 'actuals' && (
              <>
                <h6 className="mt-2">From actuals</h6>
                {actualsState === 'loading' && !actuals && <div className="small text-muted mb-2">Reading the cost data…</div>}
                {actualsState === 'error' && (
                  <ErrorBox error={{ text: `The cost data couldn't be read: ${actualsError}` }} onRetry={() => refreshActuals()} />
                )}
                {actuals && !actualsUsable && (
                  <div className="alert alert-warning py-2 small" data-testid="tm-actuals-unavailable">
                    Pricing from actuals is not available: {unavailableWhy} Use &quot;Price by hand&quot;.
                  </div>
                )}
                {actuals && actuals.preview_error && (
                  <div className="alert alert-danger py-2 small">Invoice preview failed: {String(actuals.preview_error)}</div>
                )}
                {defectsError && (
                  <div className="alert alert-secondary py-1 small">The defect list couldn&apos;t be read ({defectsError}): the Scrap drill-down shows the cost data&apos;s own view.</div>
                )}
                {notice && <div className={`alert alert-${notice.kind} py-2 small`} role="status" data-testid="tm-notice">{notice.text}</div>}
                {decision && (
                  <TmDefectDecisionDialog
                    key={`${decision.defect && decision.defect.defect_id}:${decision.mode}`}
                    locked={!!busy && busy !== 'decide'}
                    salesOrderId={salesOrderId}
                    defect={decision.defect}
                    mode={decision.mode}
                    onBusy={(b) => setBusy(b ? 'decide' : '')}
                    onCancel={() => setDecision(null)}
                    onDone={decisionDone}
                  />
                )}
                {actualsUsable && (
                  <>
                    <TmActualsLineTable
                      lines={actualsLines}
                      inputs={actInputs}
                      onChange={changeActualsLine}
                      labelOf={labelOf}
                      previewLines={previewCurrent ? previewLinesById : null}
                      suggestions={actualsBuilt.suggestions}
                      moved={moved}
                      serverErrors={lineErrors}
                      expanded={expanded}
                      onToggle={toggleExpanded}
                      onMoveValue={moveValue}
                      transferred={transferred}
                      disabled={!!busy}
                      renderScrap={(l) => (
                        <TmScrapDrilldown
                          line={l}
                          defectsData={defectsData}
                          editable={editable}
                          disabled={!!busy}
                          onDecide={(d) => setDecision({ defect: d, mode: 'toggle' })}
                          onLink={linkDefect}
                          onRemoveLink={removeLink}
                        />
                      )}
                    />
                    <TmExtrasCard
                      rows={extraRows}
                      onChange={setExtraRows}
                      minimum={minimum}
                      onMinimum={setMinimum}
                      lines={actualsLines}
                      rates={actuals.rates || {}}
                      labelOf={labelOf}
                      previewExtras={previewCurrent ? (previewForBasis.data.proposal && previewForBasis.data.proposal.extras) : null}
                      previewLines={previewCurrent ? previewLinesById : null}
                      ownPriceLines={ownPriceLines}
                      disabled={!!busy}
                    />
                  </>
                )}
              </>
            )}

            <div className="row g-2 align-items-end mb-2">
              {[['shipping_amount', 'Shipping'], ['additional_fees', 'Fees'], ['discount_amount', 'Discount']].map(([k, lab]) => (
                <div className="col-auto" key={k} style={{ width: 130 }}>
                  <label className="form-label small mb-0" htmlFor={`tm-h-${k}`}>{lab}</label>
                  <input
                    id={`tm-h-${k}`}
                    type="text"
                    inputMode="decimal"
                    className="form-control form-control-sm text-end"
                    value={headerIn[k]}
                    disabled={!!busy}
                    onChange={(e) => setHeaderIn((h) => ({ ...h, [k]: e.target.value }))}
                  />
                </div>
              ))}
              <div className="col">
                <label className="form-label small mb-0" htmlFor="tm-note">Note (optional)</label>
                <input id="tm-note" type="text" className="form-control form-control-sm" value={note} disabled={!!busy} onChange={(e) => setNote(e.target.value)} />
              </div>
            </div>

            {built.errors.length > 0 && (
              <div className="small text-danger mb-2" data-testid="tm-before-preview">
                Before Preview: {built.errors.slice(0, 4).join(' · ')}{built.errors.length > 4 ? ` · +${built.errors.length - 4} more` : ''}
              </div>
            )}

            <ErrorBox error={runError} onRetry={runError && runError.retry ? doPreview : null} />

            {previewForBasis && (
              <div className={`card mb-2 ${previewCurrent ? 'border-success' : 'border-warning'}`} data-testid="tm-preview">
                <div className="card-body py-2 small">
                  {!previewCurrent && <div className="text-warning-emphasis mb-1">{staleText}</div>}
                  <div className="d-flex flex-wrap gap-3">
                    <span>Draft #{previewForBasis.data.run_id}{basis === 'actuals' ? ' (from actuals)' : ''}</span>
                    <span>Subtotal <strong>{fmtMoney(previewForBasis.data.proposal?.computed?.subtotal)}</strong></span>
                    <span>Tax <strong>{fmtMoney(previewForBasis.data.proposal?.computed?.tax_amount)}</strong></span>
                    <span>Total <strong>{fmtMoney(previewForBasis.data.proposal?.computed?.total)}</strong></span>
                    {previewForBasis.data.delta_vs_estimate != null && <span>vs internal estimate {fmtDelta(previewForBasis.data.delta_vs_estimate)}</span>}
                    {previewForBasis.data.delta_vs_nte != null && <span>vs internal not-to-exceed {fmtDelta(previewForBasis.data.delta_vs_nte)}</span>}
                  </div>
                  {pWarnings.length > 0 && (
                    <div className="text-warning-emphasis mt-1" data-testid="tm-proposal-warnings">
                      Warnings: {pWarnings.map((w) => chipLabel(typeof w === 'string' ? w : w && w.code)).join(' · ')}
                    </div>
                  )}
                  {!previewForBasis.data.ready_to_approve && (
                    <div className="text-danger mt-1">
                      Not ready to approve:
                      {hardOpen.length > 0 && ` fix: ${hardOpen.map(chipLabel).join(', ')}`}
                      {unackedOpen.length > 0 && `${hardOpen.length > 0 ? ' ·' : ''} give a reason for: ${unackedOpen.map(chipLabel).join(', ')}`}
                    </div>
                  )}
                </div>
              </div>
            )}

            {basis === 'actuals' && actualsUsable && <ReconciliationFooter actuals={actuals} />}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {step === 'edit' && ready && (
          <>
            <span className="me-auto small text-muted">
              {rg.blocking ? 'Clear the red items and give reasons for the amber ones.' : 'Preview, then approve.'}
            </span>
            <button type="button" className="btn btn-outline-secondary" onClick={requestClose} disabled={!!busy}>Close</button>
            <button type="button" className="btn btn-primary" onClick={doPreview} disabled={!!busy || built.errors.length > 0}>
              {busy === 'preview' ? 'Previewing…' : 'Preview'}
            </button>
            <button
              type="button"
              className="btn btn-success"
              onClick={() => { setFinalizeError(null); setStep('confirm'); }}
              disabled={!!busy || !canApprove}
              title={canApprove ? 'Review, then approve with an Admin or Manager PIN' : 'Preview first; approve only what the last Preview showed as ready'}
            >
              Approve T&amp;M Pricing
            </button>
          </>
        )}
        {(step === 'done' || (!ctx && !loading)) && (
          <button type="button" className="btn btn-secondary" onClick={requestClose}>Close</button>
        )}
      </Modal.Footer>
    </>
  );
}

export default function TmPricingModal({ show, salesOrderId, orderNumber, soLines = [], onClose, onApproved }) {
  const busyRef = useRef('');
  const outcomeRef = useRef(null);   // 'unknown' while a sent finalize has no known result

  // The body mounts only while shown, so each opening starts fresh (closing forgets everything,
  // the PIN included).
  useEffect(() => {
    if (show) {
      busyRef.current = '';
      outcomeRef.current = null;
    }
  }, [show]);

  const requestClose = () => {
    if (busyRef.current) return;
    const unknown = outcomeRef.current === 'unknown';
    outcomeRef.current = null;
    if (onClose) {
      if (unknown) onClose({ outcome: 'unknown' });
      else onClose();
    }
  };

  return (
    <Modal show={!!show} onHide={requestClose} size="xl" dialogClassName="tm-pricing-dialog" backdrop="static" keyboard={false} scrollable aria-labelledby={TITLE_ID}>
      {show && (
        <ErrorBoundary
          key={salesOrderId}
          fallback={(error) => (
            <>
              <Modal.Header>
                <Modal.Title id={TITLE_ID} style={{ fontSize: '1.1rem' }}>Approve T&amp;M Pricing — {orderNumber || `SO #${salesOrderId}`}</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <div className="alert alert-danger" role="alert">
                  The T&amp;M pricing window hit an error: {(error && error.message) || String(error)}. What was typed here is lost.
                  {outcomeRef.current === 'unknown' ? ' An approval may have gone through: the order is re-read when you close this.' : ''}
                </div>
              </Modal.Body>
              <Modal.Footer>
                <button type="button" className="btn btn-secondary" onClick={() => { busyRef.current = ''; requestClose(); }}>Close</button>
              </Modal.Footer>
            </>
          )}
        >
          <TmPricingBody
            salesOrderId={salesOrderId}
            orderNumber={orderNumber}
            soLines={soLines}
            onApproved={onApproved}
            requestClose={requestClose}
            busyRef={busyRef}
            outcomeRef={outcomeRef}
          />
        </ErrorBoundary>
      )}
    </Modal>
  );
}
